// src/components/SessionTimeout.js
import '../utils/axiosConfig'; // runs the 401/403 logout logic globally
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const WARNING_MS = 60 * 1000; // show warning 60s before logout

const SessionTimeout = ({ timeout, children }) => {
  const navigate = useNavigate();
  const warnTimerId = useRef();
  const logoutTimerId = useRef();
  const countdownId = useRef();
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_MS / 1000);

  const clearAll = () => {
    clearTimeout(warnTimerId.current);
    clearTimeout(logoutTimerId.current);
    clearInterval(countdownId.current);
  };

  const logout = useCallback(() => {
    clearAll();
    setWarningOpen(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user_type');
    navigate('/login', { replace: true });
  }, [navigate]);

  const showWarning = useCallback(() => {
    setWarningOpen(true);
    setSecondsLeft(WARNING_MS / 1000);
    countdownId.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    logoutTimerId.current = setTimeout(logout, WARNING_MS);
  }, [logout]);

  const resetTimer = useCallback(() => {
    if (warningOpen) return; // don't auto-reset while warning is shown
    clearAll();
    warnTimerId.current = setTimeout(showWarning, Math.max(0, timeout - WARNING_MS));
  }, [showWarning, timeout, warningOpen]);

  const stayLoggedIn = () => {
    clearAll();
    setWarningOpen(false);
    setSecondsLeft(WARNING_MS / 1000);
    warnTimerId.current = setTimeout(showWarning, Math.max(0, timeout - WARNING_MS));
  };

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();
    return () => {
      clearAll();
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [resetTimer]);

  return (
    <>
      {children}
      {warningOpen && (
        <div style={backdropStyle} role="dialog" aria-modal="true" aria-labelledby="session-timeout-title">
          <div style={modalStyle}>
            <h3 id="session-timeout-title" style={titleStyle}>Session about to expire</h3>
            <p style={textStyle}>
              You'll be logged out in <strong>{secondsLeft}</strong> second{secondsLeft === 1 ? '' : 's'} due to inactivity.
            </p>
            <div style={actionsStyle}>
              <button type="button" onClick={stayLoggedIn} style={primaryBtnStyle}>
                Stay logged in
              </button>
              <button type="button" onClick={logout} style={secondaryBtnStyle}>
                Log out now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

SessionTimeout.defaultProps = {
  timeout: 30 * 60 * 1000, // 30 mins
};

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10, 15, 44, 0.55)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: 20,
  fontFamily: 'Arial, Helvetica, sans-serif',
};

const modalStyle = {
  background: '#ffffff',
  borderRadius: 16,
  maxWidth: 440,
  width: '100%',
  padding: '28px 28px 24px',
  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.35)',
  borderTop: '4px solid #00338D',
};

const titleStyle = {
  margin: '0 0 10px',
  color: '#00338D',
  fontSize: '1.2rem',
  fontWeight: 700,
};

const textStyle = {
  margin: '0 0 20px',
  color: '#3a3a3a',
  fontSize: '0.95rem',
  lineHeight: 1.55,
};

const actionsStyle = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
};

const primaryBtnStyle = {
  background: '#00338D',
  color: '#ffffff',
  border: 'none',
  borderRadius: 999,
  padding: '10px 20px',
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  background: '#ffffff',
  color: '#00338D',
  border: '1px solid #00338D',
  borderRadius: 999,
  padding: '10px 20px',
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
};

export default SessionTimeout;
